import { Button } from '@mantine/core';
import { IconBan, IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import classes from './NoonReportScreen.module.css';
import { formatStamp } from './NoonSections.js';
import type { VoyageReport } from './types.js';

/**
 * Raporun silme eylemleri (ADR 0077 §3), Leg / Event ve Noon'da aynı: taslakta "Delete Draft"
 * (soft delete, listeden kalkar), gönderilmiş ya da yeniden açılmış raporda "Void Report"
 * (gerekçeyle iptal, izde kalır; parola yalnız şirket ayarı açıkken). Yalnız seferin son raporunda açık; değilse düğme
 * kapalıdır ve nedeni altında yazar. Yetkiyi ve kuralı yine gemi sunucusu denetler.
 */
export function WithdrawActions({
  report,
  removable,
  busy,
  canVoid,
  onRemove,
  onVoid,
}: {
  report: VoyageReport | null;
  /** Seferin son akış raporu mu (ya da yan olay). */
  removable: boolean;
  busy: boolean;
  /** Kaynak iptali destekliyor mu (eski kaynakta yok). */
  canVoid: boolean;
  onRemove: () => void;
  onVoid: () => void;
}) {
  const { t } = useTranslation();
  const w = (key: string) => t(`voyage.withdraw.${key}`);
  if (!report) return null;
  const hint = removable ? null : <p className={classes.muted}>{w('notLast')}</p>;
  if (report.status === 'draft' && report.can?.remove !== false) {
    return (
      <>
        <Button
          size="sm"
          variant="subtle"
          color="red"
          leftSection={<IconTrash size={16} />}
          disabled={busy || !removable}
          onClick={onRemove}
        >
          {w('removeDraft')}
        </Button>
        {hint}
      </>
    );
  }
  const voidable = report.status === 'submitted' || report.status === 'reopened';
  if (voidable && canVoid && report.can?.void) {
    return (
      <>
        <Button
          size="sm"
          variant="subtle"
          color="red"
          leftSection={<IconBan size={16} />}
          disabled={busy || !removable}
          onClick={onVoid}
        >
          {w('voidReport')}
        </Button>
        {hint}
      </>
    );
  }
  return null;
}

/** İptal edilen raporun gerekçesi ve zamanı (gemide ve ofiste; ADR 0077 §3). */
export function VoidedNote({ report }: { report: VoyageReport | null }) {
  const { t } = useTranslation();
  if (report?.status !== 'void') return null;
  return (
    <p className={classes.notice} data-tone="danger" role="status">
      <IconBan size={16} aria-hidden />
      <span>
        <strong>{t('voyage.withdraw.voidedTitle')}</strong>
        <br />
        {t('voyage.withdraw.voidedBecause', {
          reason: report.voidReason ?? '—',
          at: formatStamp(report.voidedAt ?? null),
        })}
      </span>
    </p>
  );
}
